/**
 * One snapshot poller and one actions provider for everything under
 * /dashboard/bot-ops, so moving between the Operations page and a bot's page
 * does not refetch, and a decision made on one shows on the other.
 */

import React from 'react';
import { Box } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { BotOpsActionsProvider } from './BotOpsActions';
import { useSnapshot } from './hooks/useSnapshot';
import { PrefsProvider } from './hooks/usePrefs';
import { Snapshot, SnapshotMeta } from './lib/types';
import { BotOpsHttpError } from './api/botOpsClient';
import { ErrorCard, PageSkeleton, WaitingForData } from './states';
import { BotOpsChrome, ThemeShell } from './BotOpsChrome';

export type BotOpsData = ReturnType<typeof useSnapshot>;

const Ctx = React.createContext<BotOpsData | null>(null);

export function BotOpsDataProvider({ children }: { children: React.ReactNode }) {
  const [hold, setHold] = React.useState(false);
  const snap = useSnapshot({ hold });
  return (
    <Ctx.Provider value={snap}>
      <BotOpsActionsProvider canWrite={Boolean(snap.meta && snap.meta.can_write)} refresh={snap.refresh} onHoldChange={setHold}>
        <PrefsProvider>{children}</PrefsProvider>
      </BotOpsActionsProvider>
    </Ctx.Provider>
  );
}

export function useBotOpsData(): BotOpsData {
  const v = React.useContext(Ctx);
  if (!v) throw new Error('useBotOpsData must be used inside <BotOpsDataProvider>');
  return v;
}

export interface ReadySnapshot {
  snapshot: Snapshot;
  meta: SnapshotMeta | null;
  data: BotOpsData;
  canWrite: boolean;
  error: BotOpsHttpError | null;
}

/** Loading, empty and error states, then the page. Never renders zeros for a failed load. */
export function DataGate({ children }: { children: (r: ReadySnapshot) => React.ReactNode }) {
  const snap = useBotOpsData();
  if (snap.loading && !snap.data) return <Box sx={{ maxWidth: 1480, mx: 'auto', p: 3 }}><PageSkeleton /></Box>;
  if (!snap.data) {
    if (snap.error && snap.error.code === 'BOT_OPS_NO_SNAPSHOT') return <WaitingForData onRetry={snap.refresh} />;
    if (snap.error) return <ErrorCard error={snap.error} onRetry={snap.refresh} lastGoodAt={snap.lastSyncedAt} />;
    return <WaitingForData onRetry={snap.refresh} />;
  }
  return <>{children({ snapshot: snap.data, meta: snap.meta, data: snap, canWrite: Boolean(snap.meta && snap.meta.can_write), error: snap.error })}</>;
}

/** The shared shell for the nested routes. */
export function BotOpsLayout() {
  return (
    <BotOpsDataProvider>
      <ThemeShell>
        <BotOpsChrome />
        <Outlet />
      </ThemeShell>
    </BotOpsDataProvider>
  );
}
